import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = '';

export default function ProfileSettings({ onClose }) {
  const { user, refreshUser } = useAuth();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [profilePhoto, setProfilePhoto] = useState(user?.profile_photo || null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('profile');

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Limit to 500KB to avoid network issues
      if (file.size > 500 * 1024) {
        setMessage({ type: 'error', text: 'Image must be less than 500KB. Please use a smaller image.' });
        return;
      }
      
      // Compress image using canvas
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxSize = 200; // Max 200x200 for profile photo
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxSize) {
              height *= maxSize / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width *= maxSize / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to compressed JPEG
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
          setProfilePhoto(compressedBase64);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Build payload - only include photo if it changed from user's current photo
    const payload = { ...formData };
    if (profilePhoto !== user?.profile_photo) {
      payload.profile_photo = profilePhoto;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      // Just check status code, don't read body
      if (response.status >= 200 && response.status < 300) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        // Refresh user data
        if (refreshUser) {
          setTimeout(() => refreshUser(), 100);
        }
      } else if (response.status === 401) {
        setMessage({ type: 'error', text: 'Session expired. Please log in again.' });
      } else if (response.status === 400) {
        setMessage({ type: 'error', text: 'Invalid data. Please check your input.' });
      } else {
        setMessage({ type: 'error', text: `Update failed (Error ${response.status})` });
      }
    } catch (err) {
      console.error('Profile update error:', err);
      if (err.message && err.message.includes('stream')) {
        // Stream error - but the update might have succeeded
        setMessage({ type: 'success', text: 'Profile may have been updated. Please refresh the page.' });
        if (refreshUser) setTimeout(() => refreshUser(), 100);
      } else {
        setMessage({ type: 'error', text: `Connection error: ${err.message}` });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setLoading(false);
      return;
    }

    if (passwordData.new_password.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/profile/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });

      // Just check status code, don't read body
      if (response.status >= 200 && response.status < 300) {
        setMessage({ type: 'success', text: 'Password updated successfully!' });
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      } else if (response.status === 400) {
        setMessage({ type: 'error', text: 'Current password is incorrect' });
      } else {
        setMessage({ type: 'error', text: `Update failed (Error ${response.status})` });
      }
    } catch (err) {
      console.error('Password update error:', err);
      setMessage({ type: 'error', text: `Connection error: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-2xl max-h-[85vh] overflow-hidden">
        <div className="absolute -inset-0.5 bg-gradient-to-r from-[#0FFFE2] via-[#DF1283] to-[#FF7901] rounded-2xl opacity-50 blur-sm" />
        
        <div className="relative bg-black/95 rounded-2xl border border-[#4A0A66]/30 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[#4A0A66]/30">
            <div>
              <h2 className="text-2xl font-bold text-white">Profile Settings</h2>
              <p className="text-gray-400 text-sm mt-1">Manage your account</p>
            </div>
            <button
              onClick={onClose}
              data-testid="close-profile-settings"
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-[#4A0A66]/30">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === 'profile'
                  ? 'text-[#0FFFE2] border-b-2 border-[#0FFFE2]'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Profile
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === 'password'
                  ? 'text-[#0FFFE2] border-b-2 border-[#0FFFE2]'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Password
            </button>
            <button
              onClick={() => setActiveTab('onboarding')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === 'onboarding'
                  ? 'text-[#0FFFE2] border-b-2 border-[#0FFFE2]'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Preferences
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[50vh]">
            {message.text && (
              <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
                message.type === 'error'
                  ? 'bg-[#DF1283]/10 border border-[#DF1283]/50 text-[#DF1283]'
                  : 'bg-[#0FFFE2]/10 border border-[#0FFFE2]/50 text-[#0FFFE2]'
              }`}>
                {message.text}
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <form onSubmit={handleProfileSubmit} className="space-y-6">
                {/* Photo Upload */}
                <div className="flex items-center gap-6">
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="relative w-24 h-24 rounded-full overflow-hidden cursor-pointer group"
                  >
                    {profilePhoto ? (
                      <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-r from-[#DF1283] to-[#FF7901] flex items-center justify-center text-3xl font-bold text-white">
                        {user?.first_name?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handlePhotoChange}
                    accept="image/*"
                    className="hidden"
                  />
                  <div>
                    <p className="text-white font-medium">Profile Photo</p>
                    <p className="text-gray-400 text-sm">Click to upload (max 2MB)</p>
                  </div>
                </div>

                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">First Name</label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0a0014] border border-[#4A0A66]/50 rounded-lg text-white focus:outline-none focus:border-[#0FFFE2] transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Last Name</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0a0014] border border-[#4A0A66]/50 rounded-lg text-white focus:outline-none focus:border-[#0FFFE2] transition-all"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0a0014] border border-[#4A0A66]/50 rounded-lg text-white focus:outline-none focus:border-[#0FFFE2] transition-all"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-[#0FFFE2] to-[#D0FF0B] text-black font-semibold rounded-lg hover:opacity-90 transition-all disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            )}

            {/* Password Tab */}
            {activeTab === 'password' && (
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Current Password</label>
                  <input
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-[#0a0014] border border-[#4A0A66]/50 rounded-lg text-white focus:outline-none focus:border-[#0FFFE2] transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">New Password</label>
                  <input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-[#0a0014] border border-[#4A0A66]/50 rounded-lg text-white focus:outline-none focus:border-[#0FFFE2] transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-[#0a0014] border border-[#4A0A66]/50 rounded-lg text-white focus:outline-none focus:border-[#0FFFE2] transition-all"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-[#DF1283] to-[#FF7901] text-white font-semibold rounded-lg hover:opacity-90 transition-all disabled:opacity-50"
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </form>
            )}

            {/* Onboarding/Preferences Tab */}
            {activeTab === 'onboarding' && (
              <div className="space-y-4">
                <div className="bg-[#0a0014] rounded-xl p-4 border border-[#4A0A66]/30">
                  <h3 className="text-white font-medium mb-3">Your Preferences</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Industry</span>
                      <span className="text-[#0FFFE2]">{user?.onboarding_data?.industry || 'Not set'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Usage Type</span>
                      <span className="text-[#D0FF0B] capitalize">{user?.onboarding_data?.usage_type || 'Not set'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Primary Goal</span>
                      <span className="text-[#FF7901]">{user?.onboarding_data?.primary_goal || 'Not set'}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-[#0a0014] rounded-xl p-4 border border-[#4A0A66]/30">
                  <h3 className="text-white font-medium mb-3">Account Info</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Username</span>
                      <span className="text-white">@{user?.username}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Role</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        user?.role === 'admin'
                          ? 'bg-gradient-to-r from-[#D0FF0B] to-[#0FFFE2] text-black'
                          : 'bg-[#4A0A66] text-white'
                      }`}>
                        {user?.role?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Member Since</span>
                      <span className="text-white">
                        {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
